import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  updateDoc,
  increment
} from 'firebase/firestore';
import { db } from './config';

// Members collection
const getMembersCollection = () => {
  return collection(db, 'members');
};

// Point records collection
const getPointRecordsCollection = () => {
  return collection(db, 'pointRecords');
};

// Point applications collection
const getPointApplicationsCollection = () => {
  return collection(db, 'pointApplications');
};

// Point audit log collection
const getPointAuditLogCollection = () => {
  return collection(db, 'pointAuditLog');
};

// Point categories with detailed options
export const POINT_CATEGORIES = [
  {
    value: 'general-meeting',
    label: 'General Meeting Attendance',
    defaultPoints: 5,
    fixedPoints: true
  },
  {
    value: 'social-event',
    label: 'Social Event Attendance',
    defaultPoints: 20,
    fixedPoints: true
  },
  {
    value: 'practice-session',
    label: 'Practice Sessions',
    defaultPoints: 15,
    fixedPoints: true
  },
  {
    value: 'volunteering',
    label: 'Volunteering',
    defaultPoints: 5,
    perHour: true,
    requiresHours: true,
    fixedPoints: false
  },
  {
    value: 'judging-tournament',
    label: 'Judging at Tournament',
    defaultPoints: 10,
    perRound: true,
    fixedPoints: false
  },
  {
    value: 'judging-novice',
    label: 'Judging at Novice Night',
    defaultPoints: 40,
    fixedPoints: true
  },
  {
    value: 'tournament-attendance',
    label: 'Tournament Attendance (Compete)',
    defaultPoints: 40,
    fixedPoints: true
  },
  {
    value: 'additional-event',
    label: 'Each Additional Event',
    defaultPoints: 10,
    fixedPoints: true
  },
  {
    value: 'tournament-placing',
    label: 'Tournament Placing',
    defaultPoints: 25,
    fixedPoints: false,
    options: [
      { label: '1st Place', points: 25 },
      { label: '2nd Place', points: 20 },
      { label: '3rd Place', points: 15 },
      { label: '4th-6th / Quarters', points: 10 }
    ]
  },
  {
    value: 'uil-qualification',
    label: 'UIL Qualification',
    defaultPoints: 40,
    fixedPoints: false,
    options: [
      { label: 'Region', points: 40 },
      { label: 'State', points: 60 }
    ]
  },
  {
    value: 'nsda-nationals',
    label: 'NSDA Nationals Qualification',
    defaultPoints: 65,
    fixedPoints: true
  },
  {
    value: 'tfa-state',
    label: 'TFA State Qualification',
    defaultPoints: 75,
    fixedPoints: true
  },
  {
    value: 'nationals-state-placing',
    label: 'Placing at Nationals or State',
    defaultPoints: 600,
    fixedPoints: false,
    options: [
      { label: '1st Place', points: 600 },
      { label: '2nd Place', points: 500 },
      { label: '3rd Place', points: 400 },
      { label: '4th Place', points: 300 },
      { label: '5th Place', points: 200 },
      { label: '6th Place', points: 100 }
    ]
  },
  {
    value: 'deduction',
    label: 'Point Deduction',
    defaultPoints: -5,
    fixedPoints: false
  },
  {
    value: 'other',
    label: 'Other',
    defaultPoints: 0,
    fixedPoints: false
  }
];

// Create or update a member
export const saveMember = async (memberData) => {
  try {
    const membersRef = getMembersCollection();

    // Build full name from firstName and lastName if provided
    const fullName = memberData.firstName && memberData.lastName
      ? `${memberData.firstName} ${memberData.lastName}`
      : memberData.name;

    if (memberData.id) {
      // Update existing member
      const memberRef = doc(db, 'members', memberData.id);
      const updateData = {
        name: fullName || memberData.name,
        updatedAt: serverTimestamp()
      };

      // Add optional fields if provided
      if (memberData.firstName !== undefined) updateData.firstName = memberData.firstName;
      if (memberData.lastName !== undefined) updateData.lastName = memberData.lastName;
      if (memberData.email !== undefined) updateData.email = memberData.email;
      if (memberData.grade !== undefined) updateData.grade = memberData.grade;
      if (memberData.category !== undefined) updateData.category = memberData.category;
      if (memberData.studentId !== undefined) updateData.studentId = memberData.studentId;

      await updateDoc(memberRef, updateData);
      return { success: true, id: memberData.id };
    } else {
      // Create new member
      const docRef = await addDoc(membersRef, {
        name: fullName,
        firstName: memberData.firstName || '',
        lastName: memberData.lastName || '',
        email: memberData.email || '',
        grade: memberData.grade || '',
        category: memberData.category || '',
        studentId: memberData.studentId || '',
        totalPoints: 0,
        createdAt: serverTimestamp()
      });
      return { success: true, id: docRef.id };
    }
  } catch (error) {
    console.error('Error saving member:', error);
    return { success: false, error };
  }
};

// Bulk upload members from CSV data
export const bulkUploadMembers = async (membersArray) => {
  try {
    const membersRef = getMembersCollection();
    const results = {
      success: 0,
      failed: 0,
      errors: []
    };

    for (const memberData of membersArray) {
      try {
        const fullName = `${memberData.firstName} ${memberData.lastName}`.trim();

        await addDoc(membersRef, {
          name: fullName,
          firstName: memberData.firstName || '',
          lastName: memberData.lastName || '',
          email: memberData.email || '',
          grade: memberData.grade || '',
          category: memberData.category || '',
          studentId: memberData.studentId || '',
          totalPoints: 0,
          createdAt: serverTimestamp()
        });

        results.success++;
      } catch (error) {
        results.failed++;
        results.errors.push(`Failed to add ${memberData.firstName} ${memberData.lastName}: ${error.message}`);
      }
    }

    return { success: true, results };
  } catch (error) {
    console.error('Error bulk uploading members:', error);
    return { success: false, error };
  }
};

// Get all members
export const getMembers = async () => {
  try {
    const membersRef = getMembersCollection();
    const q = query(membersRef, orderBy('name'));
    const querySnapshot = await getDocs(q);
    const members = [];
    querySnapshot.forEach((doc) => {
      members.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, members };
  } catch (error) {
    console.error('Error getting members:', error);
    return { success: false, error };
  }
};

// Get member by ID
export const getMember = async (memberId) => {
  try {
    const memberRef = doc(db, 'members', memberId);
    const memberSnap = await getDoc(memberRef);

    if (!memberSnap.exists()) {
      return { success: false, error: 'Member not found' };
    }

    return { success: true, member: { id: memberSnap.id, ...memberSnap.data() } };
  } catch (error) {
    console.error('Error getting member:', error);
    return { success: false, error };
  }
};

// Delete a member
export const deleteMember = async (memberId) => {
  try {
    const memberRef = doc(db, 'members', memberId);
    await deleteDoc(memberRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting member:', error);
    return { success: false, error };
  }
};

// Create a point record
export const createPointRecord = async (recordData) => {
  try {
    const recordsRef = getPointRecordsCollection();

    const docRef = await addDoc(recordsRef, {
      memberId: recordData.memberId,
      memberName: recordData.memberName,
      category: recordData.category,
      points: recordData.points,
      notes: recordData.notes || '',
      eventName: recordData.eventName || '',
      eventDate: recordData.eventDate || '',
      hours: recordData.hours || null,
      rounds: recordData.rounds || null,
      placementOption: recordData.placementOption || null,
      createdBy: recordData.createdBy || 'admin',
      createdAt: serverTimestamp()
    });

    // Update member's total points
    const memberRef = doc(db, 'members', recordData.memberId);
    await updateDoc(memberRef, {
      totalPoints: increment(recordData.points)
    });

    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error creating point record:', error);
    return { success: false, error };
  }
};

// Get all point records
export const getPointRecords = async () => {
  try {
    const recordsRef = getPointRecordsCollection();
    const q = query(recordsRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    const records = [];
    querySnapshot.forEach((doc) => {
      records.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, records };
  } catch (error) {
    console.error('Error getting point records:', error);
    return { success: false, error };
  }
};

// Get point records for a specific member
export const getMemberPointRecords = async (memberId) => {
  try {
    const recordsRef = getPointRecordsCollection();
    const q = query(
      recordsRef,
      where('memberId', '==', memberId),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    const records = [];
    querySnapshot.forEach((doc) => {
      records.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, records };
  } catch (error) {
    console.error('Error getting member point records:', error);
    return { success: false, error };
  }
};

// Delete a point record
export const deletePointRecord = async (recordId, memberId, points) => {
  try {
    const recordRef = doc(db, 'pointRecords', recordId);
    await deleteDoc(recordRef);

    // Update member's total points (subtract the deleted points)
    const memberRef = doc(db, 'members', memberId);
    await updateDoc(memberRef, {
      totalPoints: increment(-points)
    });

    return { success: true };
  } catch (error) {
    console.error('Error deleting point record:', error);
    return { success: false, error };
  }
};

// Update a point record
export const updatePointRecord = async (recordId, recordData, oldPoints, editedBy) => {
  try {
    // Get the old record data first for audit log
    const recordRef = doc(db, 'pointRecords', recordId);
    const oldRecordSnap = await getDoc(recordRef);
    const oldRecordData = oldRecordSnap.data();

    // Update the record
    await updateDoc(recordRef, {
      category: recordData.category,
      points: recordData.points,
      notes: recordData.notes || '',
      eventName: recordData.eventName || '',
      eventDate: recordData.eventDate || '',
      hours: recordData.hours || null,
      rounds: recordData.rounds || null,
      placementOption: recordData.placementOption || null,
      updatedAt: serverTimestamp(),
      lastEditedBy: editedBy || 'admin'
    });

    // Update member's total points
    const pointsDiff = recordData.points - oldPoints;
    if (pointsDiff !== 0) {
      const memberRef = doc(db, 'members', recordData.memberId);
      await updateDoc(memberRef, {
        totalPoints: increment(pointsDiff)
      });
    }

    // Create audit log entry
    const auditLogRef = getPointAuditLogCollection();
    await addDoc(auditLogRef, {
      recordId: recordId,
      memberId: recordData.memberId,
      memberName: recordData.memberName,
      action: 'update',
      oldData: {
        category: oldRecordData.category,
        points: oldPoints,
        notes: oldRecordData.notes || '',
        eventName: oldRecordData.eventName || '',
        eventDate: oldRecordData.eventDate || '',
        hours: oldRecordData.hours || null,
        rounds: oldRecordData.rounds || null,
        placementOption: oldRecordData.placementOption || null
      },
      newData: {
        category: recordData.category,
        points: recordData.points,
        notes: recordData.notes || '',
        eventName: recordData.eventName || '',
        eventDate: recordData.eventDate || '',
        hours: recordData.hours || null,
        rounds: recordData.rounds || null,
        placementOption: recordData.placementOption || null
      },
      editedBy: editedBy || 'admin',
      timestamp: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    console.error('Error updating point record:', error);
    return { success: false, error };
  }
};

// ===== POINT APPLICATIONS =====

// Create a point application
export const createPointApplication = async (applicationData) => {
  try {
    const applicationsRef = getPointApplicationsCollection();

    const docRef = await addDoc(applicationsRef, {
      memberName: applicationData.memberName,
      studentId: applicationData.studentId || '',
      category: applicationData.category,
      notes: applicationData.notes || '',
      eventName: applicationData.eventName || '',
      eventDate: applicationData.eventDate || '',
      hours: applicationData.hours || null,
      rounds: applicationData.rounds || null,
      placementOption: applicationData.placementOption || '',
      calculatedPoints: applicationData.calculatedPoints || 0,
      status: 'pending',
      createdAt: serverTimestamp()
    });

    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error creating point application:', error);
    return { success: false, error };
  }
};

// Get all point applications
export const getPointApplications = async () => {
  try {
    const applicationsRef = getPointApplicationsCollection();
    const q = query(applicationsRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    const applications = [];
    querySnapshot.forEach((doc) => {
      applications.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, applications };
  } catch (error) {
    console.error('Error getting point applications:', error);
    return { success: false, error };
  }
};

// Approve point application
export const approvePointApplication = async (applicationId, approvedBy, memberId, points, applicationData = null) => {
  try {
    const applicationRef = doc(db, 'pointApplications', applicationId);
    const appSnap = await getDoc(applicationRef);

    if (!appSnap.exists()) {
      return { success: false, error: 'Application not found' };
    }

    const appData = appSnap.data();

    // Create point record with all application details
    await createPointRecord({
      memberId,
      memberName: appData.memberName,
      category: appData.category,
      points,
      notes: appData.notes || '',
      eventName: appData.eventName || '',
      eventDate: appData.eventDate || '',
      hours: appData.hours || null,
      rounds: appData.rounds || null,
      placementOption: appData.placementOption || '',
      createdBy: approvedBy
    });

    // Update application status
    await updateDoc(applicationRef, {
      status: 'approved',
      approvedBy,
      approvedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    console.error('Error approving point application:', error);
    return { success: false, error };
  }
};

// Reject point application
export const rejectPointApplication = async (applicationId, rejectedBy, reason = '') => {
  try {
    const applicationRef = doc(db, 'pointApplications', applicationId);
    await updateDoc(applicationRef, {
      status: 'rejected',
      rejectedBy,
      rejectionReason: reason,
      rejectedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    console.error('Error rejecting point application:', error);
    return { success: false, error };
  }
};

// Delete point application
export const deletePointApplication = async (applicationId) => {
  try {
    const applicationRef = doc(db, 'pointApplications', applicationId);
    await deleteDoc(applicationRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting point application:', error);
    return { success: false, error };
  }
};

// Delete members who have no point records
export const deleteMembersWithoutPoints = async () => {
  try {
    const membersRef = getMembersCollection();
    const recordsRef = getPointRecordsCollection();

    // Get all members
    const membersSnapshot = await getDocs(membersRef);
    const allMembers = [];
    membersSnapshot.forEach((doc) => {
      allMembers.push({ id: doc.id, ...doc.data() });
    });

    // Get all point records
    const recordsSnapshot = await getDocs(recordsRef);
    const memberIdsWithPoints = new Set();
    recordsSnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.memberId) {
        memberIdsWithPoints.add(data.memberId);
      }
    });

    // Find members without any point records
    const membersToDelete = allMembers.filter(member =>
      !memberIdsWithPoints.has(member.id) && (member.totalPoints === 0 || !member.totalPoints)
    );

    // Delete members without points
    const deletePromises = membersToDelete.map(member =>
      deleteDoc(doc(db, 'members', member.id))
    );

    await Promise.all(deletePromises);

    return {
      success: true,
      deletedCount: membersToDelete.length,
      deletedMembers: membersToDelete.map(m => m.name || `${m.firstName} ${m.lastName}`)
    };
  } catch (error) {
    console.error('Error deleting members without points:', error);
    return { success: false, error };
  }
};

// ===== AUDIT LOG =====

// Get all audit log entries
export const getAuditLog = async () => {
  try {
    const auditLogRef = getPointAuditLogCollection();
    const q = query(auditLogRef, orderBy('timestamp', 'desc'));
    const querySnapshot = await getDocs(q);
    const logs = [];
    querySnapshot.forEach((doc) => {
      logs.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, logs };
  } catch (error) {
    console.error('Error getting audit log:', error);
    return { success: false, error };
  }
};

// Get audit log entries for a specific member
export const getMemberAuditLog = async (memberId) => {
  try {
    const auditLogRef = getPointAuditLogCollection();
    const q = query(
      auditLogRef,
      where('memberId', '==', memberId),
      orderBy('timestamp', 'desc')
    );
    const querySnapshot = await getDocs(q);
    const logs = [];
    querySnapshot.forEach((doc) => {
      logs.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, logs };
  } catch (error) {
    console.error('Error getting member audit log:', error);
    return { success: false, error };
  }
};
